// Fixed-step accumulator. Sim ticks at 60Hz, render runs every rAF with interpolation alpha.

import { CONFIG } from '../config';
import { Clock } from './Clock';
import { netcodeProfiler } from '../profile/NetcodeProfiler';

export class GameLoop {
  private clock = new Clock();
  private accumulator = 0;
  private running = false;
  private rafId = 0;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastAlpha = 0;
  private rafFrameIdx = 0;

  constructor(
    private onStep: (fixedDt: number) => void,
    private onRender: (alpha: number, realDt: number) => void
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.reset();
    this.accumulator = 0;
    this.frame();
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private frame = (): void => {
    if (!this.running) return;
    // When `unrestrictFps` is on, schedule via setTimeout(0) so the loop
    // is no longer pinned to the display's VSync interval.
    if (CONFIG.sim.unrestrictFps) {
      this.timeoutId = setTimeout(this.frame, 0);
    } else {
      this.rafId = requestAnimationFrame(this.frame);
    }
    const prof = netcodeProfiler.isRecording();
    const rafStartMs = prof ? performance.now() : 0;
    const realDt = this.clock.delta();
    // Slow-motion: scale how much sim-time elapses per real second. The fixed
    // step itself stays 1/60 so determinism is unaffected — we just step less
    // often when slowed down.
    const dt = realDt * CONFIG.sim.simSpeed;
    this.accumulator += dt;
    const accPreMs = this.accumulator * 1000;
    const fixedDt = 1 / CONFIG.sim.tickHz;
    let steps = 0;
    while (this.accumulator >= fixedDt && steps < CONFIG.sim.maxSubsteps) {
      this.onStep(fixedDt);
      this.accumulator -= fixedDt;
      steps += 1;
    }
    if (this.accumulator > fixedDt) this.accumulator = fixedDt;
    this.lastAlpha = this.accumulator / fixedDt;
    const accPostMs = this.accumulator * 1000;
    this.onRender(this.lastAlpha, realDt);
    if (prof) {
      const frameDurationMs = performance.now() - rafStartMs;
      netcodeProfiler.recordRafFrame({
        idx: this.rafFrameIdx,
        realDtMs: realDt * 1000,
        substeps: steps,
        accPreMs,
        accPostMs,
        alpha: this.lastAlpha,
        frameDurationMs
      });
    }
    this.rafFrameIdx += 1;
  };
}
